import { Button, Classes, Dialog, Intent, NonIdealState } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { connect } from 'react-redux'
import client from '../../../client'
import { CurrentState } from '../../../current/types'
import { AppState } from '../../../store'
import { UprightStyle } from '../../../types'
import { ManifestPreferredItemDialog } from './ManifestPreferredItemDialog'
import { PreferredItemTable } from './PreferredItemTable'
import { ManifestPreferredItem } from '../../../types/api'

interface State {
  loading: boolean
  items: ManifestPreferredItem[]
  preferredItemDialogOpen: boolean
  preferredItemDeleteDialogOpen: boolean
  openPreferredItem: ManifestPreferredItem
}

interface Props {
  allowsAdd?: boolean
  maxTableHeight?: number
  current: CurrentState
}

const styles: UprightStyle = {
  marginBottom: { marginBottom: 8 },
  padTop: { paddingTop: 8 }
}

export class CurrentPreferredItems extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      items: [],
      preferredItemDialogOpen: false,
      preferredItemDeleteDialogOpen: false,
      openPreferredItem: null
    }
  }

  componentDidMount = () => {
    if (this.props.allowsAdd) analytics.track(`Manifest Preferred Item Management Opened`)
    this.fetchPreferredItems()
  }

  fetchPreferredItems = async () => {
    this.setState({ loading: true })
    try {
      const res = await client.getManifestPreferredItems()
      const items = res.data.manifestPreferredItems
      this.setState({
        items,
        loading: false
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      this.setState({ loading: false })
    }
  }

  onPreferredItemClick = (item: ManifestPreferredItem) => {
    this.setState({ preferredItemDialogOpen: true, openPreferredItem: item })
  }

  onPreferredItemDelete = async () => {
    try {
      await client.deleteManifestPreferredItem(this.state.openPreferredItem.id)
      analytics.track(`Manifest Preferred Item Delete`)
      this.setState({ preferredItemDeleteDialogOpen: false, openPreferredItem: null })
      this.fetchPreferredItems()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  onAddItem = () => this.setState({ preferredItemDialogOpen: true })

  onCancel = () => this.setState({ preferredItemDeleteDialogOpen: false, openPreferredItem: null })

  render() {
    const { items, loading } = this.state
    const { allowsAdd, maxTableHeight } = this.props
    const tableStyle: React.CSSProperties = {
      overflowY: 'scroll',
      maxHeight: maxTableHeight
    }
    return (
      <>
        <ManifestPreferredItemDialog
          isOpen={this.state.preferredItemDialogOpen}
          onClose={() => this.setState({ preferredItemDialogOpen: false, openPreferredItem: null })}
          preferredItem={this.state.openPreferredItem}
          onSaveComplete={this.fetchPreferredItems}
          readonly={!this.props.current?.user?.permissions.includes('settings.manifests')}
          onDelete={() => this.setState({ preferredItemDialogOpen: false, preferredItemDeleteDialogOpen: true })}
        />
        <Dialog
          onClose={() => this.setState({ preferredItemDeleteDialogOpen: false })}
          title="Delete preferred item"
          portalClassName="upright-popover-safe-dialog"
          isOpen={this.state.preferredItemDeleteDialogOpen}
        >
          <div className={Classes.DIALOG_BODY}>
            <p>Are you sure you want to delete this preferred item?</p>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.onCancel}>Cancel</Button>
              <Button intent={Intent.DANGER} onClick={this.onPreferredItemDelete}>
                Delete
              </Button>
            </div>
          </div>
        </Dialog>

        {allowsAdd && (
          <Button intent={Intent.PRIMARY} style={styles.marginBottom} onClick={this.onAddItem}>
            Add item preference
          </Button>
        )}
        <div style={tableStyle}>
          {items.length === 0 && !loading && (
            <NonIdealState icon={IconNames.NUMBERED_LIST} title="No item preferences" />
          )}
          {(items.length > 0 || loading) && (
            <PreferredItemTable loading={loading} items={items} onSelectItem={this.onPreferredItemClick} />
          )}
        </div>
      </>
    )
  }
}

export const ManifestPreferredItems = connect((state: AppState) => ({ current: state.current }))(CurrentPreferredItems)
